import React, { Fragment } from 'react';

import { Select } from '@aline/common/util/selectors';
import { IconButton, Typography } from '@mui/material';
import { useTextTruncated } from 'src/hooks/dom-hooks';
import { makeStyles } from 'tss-react/mui';

import { AlineButton } from '../generic/AlineButton';
import { AlineTooltip } from '../generic/AlineTooltip';
import { panelHeaderHeightPx } from '../generic/PanelHeader';
import { CloseIcon } from '../icons/CloseIcon';

const pageHeaderHeightPx = panelHeaderHeightPx;
const useStyles = makeStyles()(theme => ({
  appBar: {
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.appColors.rule}`,
    boxSizing: 'border-box',
    height: pageHeaderHeightPx,
    width: '100%',
  },
  /**
   * Equal side tracks keep the step numbers centered. A long template title
   * stays in the leading track and ellipsizes instead of painting across them.
   */
  appBarBody: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
    height: '100%',
    padding: theme.spacing(0.5, 1),
    width: '100%',
  },
  closeButton: {
    flexShrink: 0,
    padding: theme.spacing(0.5),
  },
  closeIcon: {
    height: 16,
    width: 16,
  },
  leading: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    minWidth: 0,
    overflow: 'hidden',
  },
  pageTitle: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  trailing: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    minWidth: 0,
  },
}));

type PageHeaderProps = {
  activeStep: number;
  next: (e: React.MouseEvent) => void;
  onClose: () => void;
  pageTitle: string;
  steps: number[];
  back?: (e: React.MouseEvent) => void;
  backDisabledReason?: string;
  cancelDataCy?: string;
  primaryActionLoading?: boolean;
  primaryActionTitle?: string;
  primaryDataCy?: string;
  primaryDisabledReason?: string;
};

export function PageHeader(props: PageHeaderProps) {
  const { classes } = useStyles();

  return (
    <>
      <div className={classes.appBar}>
        <PageHeaderBody {...props} />
      </div>
    </>
  );
}

export function PageHeaderBody({
  activeStep,
  back,
  backDisabledReason = '',
  cancelDataCy,
  next,
  onClose,
  pageTitle,
  primaryActionLoading,
  primaryActionTitle = 'Continue',
  primaryDataCy = Select.components.quickStart.button.continue,
  primaryDisabledReason = '',
  steps,
}: PageHeaderProps) {
  const { classes } = useStyles();
  const { isTruncated, ref: pageTitleRef } = useTextTruncated<HTMLElement>();
  const isPrimaryDisabled = Boolean(primaryDisabledReason);

  return (
    <div className={classes.appBarBody}>
      <div className={classes.leading}>
        <IconButton className={classes.closeButton} data-cy={cancelDataCy} onClick={onClose}>
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
        <AlineTooltip arrow placement="bottom" title={isTruncated ? pageTitle : ''}>
          <Typography ref={pageTitleRef} noWrap className={classes.pageTitle} variant="subtitle1">
            {pageTitle}
          </Typography>
        </AlineTooltip>
      </div>
      <Stepper activeStep={activeStep} steps={steps} />
      <div className={classes.trailing}>
        {back && (
          <AlineTooltip arrow placement="bottom" title={backDisabledReason}>
            <div>
              <AlineButton
                color="primary"
                disabled={Boolean(backDisabledReason || primaryActionLoading)}
                variant="outlined"
                onClick={back}
              >
                Go Back
              </AlineButton>
            </div>
          </AlineTooltip>
        )}
        <AlineTooltip arrow placement="bottom" title={primaryDisabledReason}>
          <div>
            <AlineButton
              color="primary"
              data-cy={primaryDataCy}
              disabled={isPrimaryDisabled}
              loading={primaryActionLoading}
              variant="contained"
              onClick={next}
            >
              {primaryActionTitle}
            </AlineButton>
          </div>
        </AlineTooltip>
      </div>
    </div>
  );
}

const useStepperStyles = makeStyles()(theme => ({
  connector: {
    backgroundColor: theme.appColors.rule,
    flexShrink: 0,
    height: 1,
    width: 48,
  },
  steps: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
    justifyContent: 'center',
  },
}));

interface StepperProps {
  activeStep: number;
  steps: number[];
}

export function Stepper({ activeStep, steps }: StepperProps) {
  const { classes } = useStepperStyles();

  return (
    <div className={classes.steps}>
      {steps.map((number, index) => {
        const isLast = index === steps.length - 1;

        return (
          <Fragment key={index}>
            <Step key={index} active={activeStep === number} step={number} />
            {!isLast && <div className={classes.connector} />}
          </Fragment>
        );
      })}
    </div>
  );
}

const usePageStepsStyles = makeStyles<{ active: boolean }>()((theme, { active }) => ({
  step: {
    alignItems: 'center',
    backgroundColor: active ? `#324A52` : undefined,
    border: !active ? theme.appBorders.outline : `1px solid transparent`,
    borderRadius: '50%',
    boxSizing: 'border-box',
    color: active ? theme.palette.common.white : undefined,
    display: 'flex',
    height: 32,
    justifyContent: 'center',
    width: 32,
  },
}));

export function Step({ active, step }: { active: boolean; step: number }) {
  const { classes } = usePageStepsStyles({ active });

  return <Typography className={classes.step}>{step}</Typography>;
}
